import {
    EntryLike,
    calculateEntryMetrics,
    calculateMealMetrics,
    getMetrics,
    getRoundedCalculations,
} from "@client/calculations";
import {Eyebrow, Headline, SelectChevron} from "@client/components/Chrome";
import type {Props as EntryCardProps} from "@client/components/EntryCard";
import {
    Bubble,
    MealSelector,
    getMealOptionsForDay,
} from "@client/components/MealSelector";
import {NutritionCard} from "@client/components/NutritionCard";
import {FIELD, HAIRLINE, INK} from "@client/components/styles";
import {
    LATE_MEAL_HOUR,
    deriveMealHour,
    generateTimeOptions,
    hourToName,
} from "@client/features/journal";
import * as orm from "@client/features/orm";
import {DecimalInput, Label} from "@client/forms";
import * as forms from "@client/forms";
import * as widgets from "@client/forms/widgets";
import * as hardware from "@client/hardware";
import type {CornerToggle} from "@client/router";
import * as analytics from "@client/shared/analytics";
import {useAppDispatch, useAppSelector} from "@client/store";
import {formatUnit} from "@client/utils";
import {Listbox, ListboxButton, ListboxOption, ListboxOptions} from "@headlessui/react";
import {CheckIcon} from "@heroicons/react/20/solid";
import {classNames, server} from "@reactivated";
import Decimal from "decimal.js";
import {produce} from "immer";
import React from "react";

interface StageState<TEntry extends EntryLike> {
    method: "now" | "cupboard";
    values: Record<
        UUID,
        {quantity?: string | null; unit?: string | null; isRemoved?: boolean} | null
    >;
    entries: TEntry[];
    // Prediction selections state - normalized with entries
    predictionSelection?: {
        rootLocator: string; // The root entry we're predicting from
        selectedPatternHash: string | null; // Currently selected pattern/column
        // Selected entries are derived from entries array, not stored separately
    };
}

interface Props<TEntry extends EntryLike> {
    cardComponent?: React.ComponentType<EntryCardProps>;
    readOnly?: true;
    divideByServings?: DecimalValue;
    entries: StageState<TEntry>["entries"];
    extra?: (handler: {uuid: UUID; isRemoved: boolean}) => React.ReactNode;
    target:
        | {
              type: "RECIPE";
              recipe: server.journal.nutrition.Recipe.output;
          }
        | {
              type: "DAY";
              day: server.journal.nutrition.Day.output;
              currentMeal?: server.journal.nutrition.StagedMeal.output;
          }
        | null;
    preSelectedMealName?: string;
    // Renders a "+ Add ingredients" row at the foot of the ingredient list.
    addIngredients?: () => unknown;
}

export interface Handler<TEntry extends EntryLike> {
    mealModeToggle: CornerToggle | undefined;
    entryMetrics: React.ReactNode;
    mealSelector: React.ReactNode;
    hasErrors: boolean;
    data: StageState<TEntry>;
    entries: React.ReactNode;
    ingredients: React.ReactNode;
    singleEntry: React.ReactNode;
    bulkEntries: React.ReactNode;
    validateMeal: () => false | server.journal.nutrition.StagedMeal.output;
    validate: () => {
        meal: server.journal.nutrition.StagedMeal.output;
        entries: TEntry[];
        single: TEntry;
    } | null;
    calculations: ReturnType<typeof getRoundedCalculations>;
    setEntries: (entries: TEntry[]) => void;
    syncEntries: (entries: TEntry[]) => void;
    addEntry: (entry: TEntry) => void;
    removeEntry: (entry: TEntry) => void;
    setMethod: (method: "now" | "cupboard") => void;
    entriesWithValues: TEntry[];
    bulkEntriesContent: (filter: (entry: TEntry) => boolean) => React.ReactNode;
    // Prediction selection handlers
    setPredictionSelection: (
        selection: StageState<TEntry>["predictionSelection"],
    ) => void;
    updatePredictionPattern: (hash: string | null) => void;
}

// Entry cards stacked inside one white card, the list's dividers clipped to
// its corners.
const CARD_LIST = "rounded-[14px] bg-white px-4 py-1";

// One line in an ingredient or bulk list: name and energy, then the macros
// with the quantity controls and a quiet remove.
type MetricEntry = Parameters<typeof calculateEntryMetrics>[0]["entry"] & {
    uuid: UUID;
    name: string;
};

function StageRowQuantity(props: {
    entry: MetricEntry;
    quantity: NonNullable<EntryCardProps["quantityAndUnitHandler"]>;
    isRemoved: boolean;
}) {
    const {entry, quantity} = props;
    const amount = forms.useField(
        {kind: "decimal", name: `quantity-${entry.uuid}`, nullable: true},
        {value: quantity.quantity.value, onChange: quantity.quantity.onChange},
    );
    const unit = forms.useField(
        {
            kind: "select",
            widget: "select",
            options: quantity.unit.options.map((option): [string, string] => [
                option,
                formatUnit(option),
            ]),
        },
        {value: quantity.unit.value, onChange: quantity.unit.onChange},
    );

    return (
        <>
            {/* A recipe ingredient is stored per batch and edited
                per serving: the multiplier converts both ways. */}
            <widgets.ConversionInput
                key={`${unit.value}-${props.isRemoved}`}
                {...amount}
                ariaLabel={`Quantity for ${entry.name}`}
                unit={{
                    condition: true,
                    multiplier: quantity.quantity.multiplier ?? "1",
                    suffixes: ["", ""],
                }}
                className="h-8 w-[50px] rounded-[9px] bg-[oklch(0.35_0.04_280/0.05)] text-center text-[13px] outline-none focus:ring-[1.5px] focus:ring-[oklch(0.51_0.21_278/0.3)]"
            />
            <span className="relative flex h-8 items-center gap-1.5 rounded-[9px] bg-[oklch(0.35_0.04_280/0.05)] px-2.5 text-[12.5px] text-[oklch(0.4_0.03_280)]">
                <forms.Widget
                    field={unit}
                    aria-label={`Unit for ${entry.name}`}
                    className="absolute inset-0 appearance-none opacity-0"
                />
                {formatUnit(unit.value)}
                <SelectChevron className="size-3.5" />
            </span>
        </>
    );
}

export const StageRow = (props: {
    entry: MetricEntry;
    quantity: EntryCardProps["quantityAndUnitHandler"];
    onRemove: () => void;
    isRemoved: boolean;
    useKilojoules: boolean;
    hiddenMacros: boolean;
}) => {
    const {entry, quantity} = props;
    const rounded = getRoundedCalculations(
        calculateEntryMetrics({
            entry,
            quantity: quantity?.quantity.value ?? entry.quantity,
            unit: quantity?.unit.value ?? entry.unit,
        }),
        props.useKilojoules,
    );
    return (
        <div
            className={classNames(
                "border-b py-[13px] last:border-b-0",
                HAIRLINE,
                props.isRemoved && "opacity-40",
            )}
        >
            <div className="flex items-baseline gap-2.5">
                <span className="flex-1 text-[15px] font-normal">{entry.name}</span>
                {props.hiddenMacros == false && (
                    <span className="text-[15px] font-light">
                        {rounded.calories ?? "0"}{" "}
                        <span className="text-[11px] text-[oklch(0.6_0.02_280)]">
                            {props.useKilojoules ? "kJ" : "kcal"}
                        </span>
                    </span>
                )}
            </div>
            <div className="mt-[9px] flex items-center gap-2">
                <span
                    className={classNames(
                        "flex-1 text-[12.5px] font-light",
                        "text-[oklch(0.52_0.03_280)]",
                    )}
                >
                    {props.hiddenMacros == false && (
                        <>
                            Protein {rounded.protein} · Fat {rounded.fat} · Carbs{" "}
                            {rounded.carbs}
                        </>
                    )}
                </span>
                {quantity != null && (
                    <StageRowQuantity
                        entry={entry}
                        quantity={quantity}
                        isRemoved={props.isRemoved}
                    />
                )}
                <button
                    type="button"
                    aria-label={
                        props.isRemoved
                            ? `Restore ${entry.name}`
                            : `Remove ${entry.name}`
                    }
                    onClick={() => {
                        void hardware.haptic();
                        props.onRemove();
                    }}
                    className="pl-1 text-[18px] leading-none text-[oklch(0.7_0.02_280)]"
                >
                    ×
                </button>
            </div>
        </div>
    );
};

function ServingQuantity(props: {
    value: DecimalValue | null;
    onChange: (value: DecimalValue | null) => void;
}) {
    const quantity = forms.useField(
        {kind: "decimal", name: "quantity", nullable: true},
        {value: props.value, onChange: props.onChange},
    );

    return (
        <DecimalInput
            {...quantity}
            ariaLabel="quantity"
            className={classNames(
                FIELD,
                "h-[38px] w-[68px] text-center text-[17px] font-normal outline-none focus:ring-[1.5px] focus:ring-[oklch(0.51_0.21_278_/_0.3)]",
            )}
        />
    );
}

function ServingUnit(props: {
    entry: Pick<EntryLike, "units" | "additional_units" | "unit">;
    onChange: (unit: string) => void;
}) {
    const {entry} = props;
    const unit = forms.useField(
        {
            kind: "select",
            widget: "select",
            name: "unit",
            options: entry.units.map((option): [string, string] => [
                option.unit,
                formatUnit(option.unit),
            ]),
        },
        {value: entry.unit, onChange: props.onChange},
    );

    if (entry.units.length < 6 && entry.additional_units.length == 0) {
        return (
            <div className="contents">
                {unit.options.map(([value, label]) => (
                    <Bubble
                        key={value}
                        label={label}
                        selected={unit.value === value}
                        onClick={() => unit.onChange(value)}
                    />
                ))}
            </div>
        );
    }

    return (
        <label
            className={classNames(
                FIELD,
                "relative flex h-[38px] items-center gap-2.5 pr-2.5 pl-3.5 text-[14px] font-normal",
            )}
        >
            <forms.Widget
                field={unit}
                aria-label="unit"
                className="absolute inset-0 appearance-none opacity-0"
            />
            {formatUnit(unit.value)}
            <SelectChevron />
        </label>
    );
}

interface MealEditorState {
    mealMode: "MANUAL" | "AUTO_CREATED";
    suggestedHour: number;
    manualMealName: string | null;
    error?: string | null;
}

interface MealEditorHandler {
    mealSelector: React.ReactNode;
    // The header corner: named or timed meals. Absent when the target has
    // no meal to pick.
    mealModeToggle: CornerToggle | undefined;
    validateMeal: () => false | server.journal.nutrition.StagedMeal.output;
    state: MealEditorState;
    setState: React.Dispatch<React.SetStateAction<MealEditorState>>;
    error?: string | null;
}

interface MealEditorProps {
    target:
        | {
              type: "RECIPE";
              recipe: server.journal.nutrition.Recipe.output;
          }
        | {
              type: "DAY";
              day: server.journal.nutrition.Day.output;
              currentMeal?: server.journal.nutrition.StagedMeal.output;
          }
        | null;
    method?: "now" | "cupboard";
    preSelectedMealName?: string;
}

export function useMealEditor(props: MealEditorProps): MealEditorHandler {
    const dispatch = useAppDispatch();
    const profile = useAppSelector(orm.getProfile);

    const defaultMeal =
        props.target?.type == "DAY"
            ? (props.target.day.meals
                  .filter((m) => m.name != "__SUPPLEMENTS__")
                  .filter((m) => m.type != "AUTO_CREATED")
                  .at(-1)?.name ??
              getMealOptionsForDay(props.target.day).allMeals[0]?.value)
            : null;

    const mealName =
        props.target?.type == "DAY"
            ? (props.target.currentMeal?.name ?? defaultMeal)
            : null;

    const initialMealMode =
        (props.target?.type == "DAY" ? props.target.currentMeal?.type : null) ??
        profile?.settings.meals ??
        "MANUAL";

    const initialHour =
        props.target?.type != "DAY"
            ? new Date().getHours()
            : ((props.target.currentMeal?.type == "AUTO_CREATED"
                  ? props.target.currentMeal.hour
                  : null) ?? deriveMealHour(new Date(), props.target.day.key));

    const [state, setState] = React.useState<MealEditorState>({
        suggestedHour: initialHour,
        mealMode: initialMealMode,
        manualMealName: mealName,
        error: null,
    });

    const changeMealMode = () => {
        const nextMode = state.mealMode == "AUTO_CREATED" ? "MANUAL" : "AUTO_CREATED";
        setState((prev) => ({...prev, mealMode: nextMode}));

        void analytics.track("CHANGE_MEAL_MODE", {
            meal_mode: nextMode,
        });

        if (profile == null) {
            return;
        }

        const updatedProfile = produce(profile, (draft) => {
            draft.settings.meals = nextMode;
        });
        dispatch({type: "SET_PROFILE", profile: updatedProfile});
        void server.core.rpc.update_settings(updatedProfile.settings);
    };

    const changeStagedMeal = (meal: string | null) => {
        setState((prev) => ({...prev, manualMealName: meal}));
    };

    const changeAutomaticHour = (hour: number) => {
        setState((prev) => ({...prev, suggestedHour: hour}));
    };

    const timeOptions = generateTimeOptions().map((option) => {
        return {
            ...option,
            entriesCount:
                props.target?.type === "DAY" &&
                props.target.day.meals.find(
                    (meal) =>
                        meal.type === "AUTO_CREATED" && meal.hour === option.value,
                )?.entries.length,
        };
    });

    const selectedTime =
        timeOptions.find((opt) => opt.value === state.suggestedHour) ?? timeOptions[0];

    const mealSelector =
        props.preSelectedMealName == null &&
        props.method !== "cupboard" &&
        props.target?.type == "DAY" &&
        (state.mealMode == "AUTO_CREATED" ? (
            <div>
                <Eyebrow className="mb-[7px]">Logged at</Eyebrow>
                <Listbox
                    aria-label="Meal time"
                    value={selectedTime}
                    onChange={(option) => changeAutomaticHour(option.value)}
                >
                    <div className="relative">
                        <ListboxButton
                            // This needs to be onTouchStart or won't work on iOS.
                            onTouchStart={() => {
                                void hardware.haptic();
                                setTimeout(() => {
                                    const selectedElement = document.querySelector(
                                        `[data-scroll-value="${selectedTime.value}"]`,
                                    );
                                    if (selectedElement) {
                                        selectedElement.scrollIntoView({
                                            behavior: "smooth",
                                            block: "center",
                                        });
                                    }
                                }, 0);
                            }}
                            className={classNames(
                                FIELD,
                                "flex h-[38px] cursor-default items-center gap-2.5 pr-2.5 pl-3.5 text-left focus-visible:outline-2 focus-visible:outline-[oklch(0.51_0.21_278)]",
                            )}
                        >
                            <span className="text-[14px] font-normal">
                                {selectedTime.label}
                                {selectedTime.value >= LATE_MEAL_HOUR && (
                                    <sup
                                        aria-label="next day"
                                        className="text-[10px] font-medium text-[#5546e5]"
                                    >
                                        +1
                                    </sup>
                                )}
                            </span>
                            <SelectChevron />
                        </ListboxButton>
                        <ListboxOptions
                            anchor="bottom start"
                            transition
                            className={classNames(
                                "absolute z-10 mt-1 [--anchor-max-height:15rem] max-h-60 w-48 overflow-auto rounded-md bg-white py-1 text-base shadow-lg outline-solid outline-1 outline-black/5 data-closed:data-leave:opacity-0 data-leave:transition data-leave:duration-100 data-leave:ease-in sm:text-sm",
                                // open == false && "hidden",
                            )}
                        >
                            {timeOptions.map((option) => (
                                <React.Fragment key={option.value}>
                                    {option.value === LATE_MEAL_HOUR && (
                                        <div className="mx-3 mt-2 mb-1 border-t border-stone-200 pt-2 text-[10px] font-medium tracking-widest text-gray-400 uppercase select-none">
                                            After midnight
                                        </div>
                                    )}
                                    <ListboxOption
                                        value={option}
                                        onClick={() => {
                                            void hardware.haptic();
                                        }}
                                        data-scroll-value={option.value}
                                        className="group relative cursor-default py-2 pr-9 pl-3 text-gray-900 select-none data-focus:bg-indigo-600 data-focus:text-white data-focus:outline-hidden"
                                    >
                                        <span className="block truncate font-normal group-data-selected:font-semibold">
                                            {option.label}
                                            {option.entriesCount != null && (
                                                <span className="text-xs font-normal text-gray-500 group-data-active:text-white">
                                                    <span className="mx-2">•</span>
                                                    {option.entriesCount}{" "}
                                                    {option.entriesCount == 1
                                                        ? "entry"
                                                        : "entries"}
                                                </span>
                                            )}
                                        </span>
                                        <span className="absolute inset-y-0 right-0 flex items-center pr-4 text-indigo-600 group-data-focus:text-white group-[:not([data-selected])]:hidden">
                                            <CheckIcon
                                                aria-hidden="true"
                                                className="size-5"
                                            />
                                        </span>
                                    </ListboxOption>
                                </React.Fragment>
                            ))}
                        </ListboxOptions>
                    </div>
                </Listbox>
            </div>
        ) : props.target.type == "DAY" ? ( // oxlint-disable-line typescript/no-unnecessary-condition
            <div>
                <Eyebrow className="mb-[7px]">Meal</Eyebrow>
                <MealSelector
                    day={props.target.day}
                    onMealChange={changeStagedMeal}
                    value={state.manualMealName}
                    error={state.error ?? null}
                />
            </div>
        ) : null);

    const validateMeal = (): false | server.journal.nutrition.StagedMeal.output => {
        if (props.preSelectedMealName != null) {
            return {name: props.preSelectedMealName, type: "MANUAL", hour: null};
        }
        if (state.mealMode == "AUTO_CREATED") {
            return {
                name: hourToName(state.suggestedHour),
                type: state.mealMode,
                hour: state.suggestedHour,
            };
        }
        if (
            props.method != "cupboard" &&
            props.target?.type == "DAY" &&
            (state.manualMealName ?? "").trim() === ""
        ) {
            setState((prev) => ({...prev, error: "A meal name is required"}));
            return false;
        }

        return {name: state.manualMealName ?? "", type: state.mealMode, hour: null};
    };

    const mealModeToggle: CornerToggle | undefined =
        mealSelector == null || mealSelector === false
            ? undefined
            : {
                  kind: "toggle",
                  label: "Meals",
                  options: [
                      {value: "MANUAL", label: "Named"},
                      {value: "AUTO_CREATED", label: "Timed"},
                  ],
                  value: state.mealMode,
                  onChange: (value) => {
                      if (value !== state.mealMode) {
                          changeMealMode();
                      }
                  },
              };

    return {
        mealSelector,
        mealModeToggle,
        validateMeal,
        state,
        setState,
    };
}

export function useStage<
    TEntry extends EntryLike = server.journal.nutrition.InlineEntry.output,
>(props: Props<TEntry>): Handler<TEntry> {
    const useKilojoules = forms.useUseKilojoules();
    const hasHiddenMacros = forms.useHasHiddenMacros();

    const [errors, setErrors] = React.useState<{
        meal?: string | null;
        quantity?: string | null;
    }>({});

    function getInitialValues(entries: Props<TEntry>["entries"]) {
        return produce<StageState<TEntry>["values"]>({}, (draftState) => {
            for (const entry of entries) {
                draftState[entry.uuid] = {
                    quantity: entry.quantity,
                    unit: entry.unit,
                };

                for (const ingredient of entry.expanded_recipe) {
                    draftState[ingredient.uuid] = {
                        quantity: ingredient.quantity,
                        unit: ingredient.unit,
                    };
                }
            }
        });
    }

    const [stage, setStage] = React.useState<StageState<TEntry>>({
        method: "now",
        entries: props.entries,
        values: getInitialValues(props.entries),
        predictionSelection: undefined,
    });

    const mealEditor = useMealEditor({
        target: props.target,
        method: stage.method,
        preSelectedMealName: props.preSelectedMealName,
    });

    const hasErrors = Object.values(errors).some((error) => error != null);

    const setEntries = (entries: StageState<TEntry>["entries"]) => {
        // TODO: this won't work for setting entries that are recipes.
        setStage((prev) => ({...prev, entries, values: getInitialValues(entries)}));
    };

    const syncEntries = (entries: StageState<TEntry>["entries"]) => {
        // Syncs entries into stage, preserving values for existing/removed entries.
        // - prev.values spread on top preserves edited values and values for removed entries (for re-adding)
        // - getInitialValues provides defaults for new entries
        setStage((prev) => ({
            ...prev,
            entries,
            values: {...getInitialValues(entries), ...prev.values},
        }));
    };

    const setPredictionSelection = (
        selection: StageState<TEntry>["predictionSelection"],
    ) => {
        setStage((prev) => ({...prev, predictionSelection: selection}));
    };

    const updatePredictionPattern = (hash: string | null) => {
        setStage((prev) => ({
            ...prev,
            predictionSelection: prev.predictionSelection
                ? {
                      ...prev.predictionSelection,
                      selectedPatternHash: hash,
                  }
                : undefined,
        }));
    };

    const addEntry = (entry: StageState<TEntry>["entries"][number]) => {
        setStage((prev) => {
            const newEntries = [...prev.entries, entry];
            return {
                ...prev,
                entries: newEntries,
                values: getInitialValues(newEntries),
            };
        });
    };

    const removeEntry = (entry: StageState<TEntry>["entries"][number]) => {
        setStage((prev) => {
            const newEntries = prev.entries.filter((e) => e.uuid != entry.uuid);
            return {
                ...prev,
                entries: newEntries,
                values: getInitialValues(newEntries),
            };
        });
    };

    const entriesWithValues = stage.entries.map((entry) =>
        produce(entry, (draftState) => {
            draftState.quantity = stage.values[entry.uuid]?.quantity as string;
            draftState.unit = stage.values[entry.uuid]?.unit ?? draftState.unit;

            for (const ingredient of draftState.expanded_recipe) {
                ingredient.quantity = stage.values[ingredient.uuid]?.quantity as string;
                ingredient.unit = stage.values[ingredient.uuid]?.unit as string;
            }

            const recipeCalculations = calculateMealMetrics({
                entries: draftState.expanded_recipe.filter(
                    (ingredient) =>
                        stage.values[ingredient.uuid]?.isRemoved != true &&
                        stage.values[ingredient.uuid]?.quantity != null,
                ),
            });

            if (draftState.expanded_recipe.length > 0) {
                for (const [metric, metricPg] of getMetrics({useKilojoules: false})
                    .ALL_METRICS) {
                    draftState[metricPg] =
                        recipeCalculations[metric]
                            ?.dividedBy(draftState.container_servings)
                            .toString() ?? null;
                }
            }
        }),
    );

    const single = [entriesWithValues[0], null][0];

    if (single == null) {
        return {
            entryMetrics: <></>,
            hasErrors,
            mealSelector: mealEditor.mealSelector,
            mealModeToggle: mealEditor.mealModeToggle,
            data: stage,
            entries: <></>,
            ingredients: <></>,
            singleEntry: <></>,
            bulkEntries: <></>,
            validateMeal: mealEditor.validateMeal,
            validate: () => null,
            calculations: getRoundedCalculations({}, false),
            setEntries,
            syncEntries,
            entriesWithValues,
            addEntry,
            removeEntry,
            setMethod: () => {},
            bulkEntriesContent: () => <></>,
            setPredictionSelection,
            updatePredictionPattern,
        };
    }

    const mealCalculations = getRoundedCalculations(
        calculateMealMetrics({
            entries: entriesWithValues
                .filter((entry) => stage.values[entry.uuid]?.isRemoved != true)
                .map((entry) => ({
                    entry: entry,
                    quantity: entry.quantity,
                    unit: entry.unit,
                })),
        }),
        useKilojoules,
        props.divideByServings,
    );

    const shouldShowWholeContainer = new Decimal(single.container_servings).greaterThan(
        1,
    );

    const wholeContainer = new Decimal(single.container_servings).times(
        single.units.find((u) => u.unit == single.unit)!.multiplier,
    );
    const hasWholeContainerQuantity =
        (single.quantity as string | null) != null &&
        new Decimal(single.quantity).equals(wholeContainer);

    const bindQuantity = (uuid: UUID) => (quantity: string | null) => {
        setStage((prev) =>
            produce(prev, (draftState) => {
                draftState.values[uuid] = {
                    ...draftState.values[uuid],
                    quantity,
                };
            }),
        );
    };

    const bindUnit =
        (uuid: UUID, units: EntryLike["units"]) => (nextUnitValue: string) => {
            const staged = stage.values[uuid];
            const previousUnit = units.find((u) => u.unit == staged?.unit)!;
            const nextUnit = units.find((u) => u.unit == nextUnitValue)!;
            const previousQuantityMultiplier = previousUnit.multiplier;
            const currentQuantityMultiplier = nextUnit.multiplier;

            const convertedQuantity = new Decimal(staged?.quantity ?? 0)
                .dividedBy(previousQuantityMultiplier)
                .times(currentQuantityMultiplier);

            const isContinuousPreviousUnit =
                [
                    "g",
                    "mg",
                    "mcg",
                    "ml",
                    "fl oz",
                    "oz",
                    "lb",
                    "cup",
                    "tsp",
                    "tbsp",
                ].includes(previousUnit.unit) || previousUnit.unit.includes("cup");
            const isContinuousNextUnit =
                [
                    "g",
                    "mg",
                    "mcg",
                    "ml",
                    "fl oz",
                    "oz",
                    "lb",
                    "cup",
                    "tsp",
                    "tbsp",
                ].includes(nextUnit.unit) || nextUnit.unit.includes("cup");
            const nextQuantityValue =
                isContinuousNextUnit || isContinuousPreviousUnit
                    ? convertedQuantity.toString()
                    : staged?.quantity;

            // TODO: this is where smart logic should go so we don't get lossy conversion from 1 to 28 to 0.999 etc.
            setStage((prev) =>
                produce(prev, (draftState) => {
                    draftState.values[uuid] = {
                        ...draftState.values[uuid],
                        quantity: nextQuantityValue,
                        unit: nextUnit.unit,
                    };
                }),
            );
        };

    const bindRemove = (uuid: UUID) => () => {
        setStage((prev) =>
            produce(prev, (draftState) => {
                draftState.values[uuid] = {
                    ...draftState.values[uuid],
                    isRemoved: !(draftState.values[uuid]?.isRemoved ?? false),
                };
            }),
        );
    };
    const singleEntryCalculations = getRoundedCalculations(
        calculateEntryMetrics({
            entry: single,
            quantity: single.quantity,
            unit: single.unit,
        }),
        useKilojoules,
    );

    const entryMetrics =
        hasHiddenMacros == false ? (
            <NutritionCard
                calculations={singleEntryCalculations}
                useKilojoules={useKilojoules}
                eyebrow="this serving"
            />
        ) : null;

    const CardComponent = props.cardComponent;

    const bulkEntriesContent = (filter: (entry: TEntry) => boolean) =>
        entriesWithValues.filter(filter).map((entry, index) =>
            CardComponent == null ? (
                <StageRow
                    key={index}
                    entry={entry}
                    useKilojoules={useKilojoules}
                    hiddenMacros={hasHiddenMacros}
                    isRemoved={stage.values[entry.uuid]?.isRemoved ?? false}
                    onRemove={bindRemove(entry.uuid)}
                    quantity={{
                        unit: {
                            onChange: bindUnit(entry.uuid, entry.units),
                            value: entry.unit,
                            options: entry.units.map((unit) => unit.unit),
                        },
                        quantity: {
                            onChange: bindQuantity(entry.uuid),
                            value: entry.quantity,
                        },
                    }}
                />
            ) : (
                <CardComponent
                    key={index}
                    entry={{entry, quantity: entry.quantity, unit: entry.unit}}
                    extra={props.extra?.({
                        uuid: entry.uuid,
                        isRemoved: stage.values[entry.uuid]?.isRemoved ?? false,
                    })}
                    quantityAndUnitHandler={
                        props.readOnly
                            ? undefined
                            : {
                                  unit: {
                                      onChange: bindUnit(entry.uuid, entry.units),
                                      value: entry.unit,
                                      options: entry.units.map((unit) => unit.unit),
                                  },
                                  quantity: {
                                      onChange: bindQuantity(entry.uuid),
                                      value: entry.quantity,
                                  },
                              }
                    }
                    removeHandler={
                        props.readOnly
                            ? undefined
                            : {
                                  onRemove: bindRemove(entry.uuid),
                                  isRemoved:
                                      stage.values[entry.uuid]?.isRemoved ?? false,
                              }
                    }
                    hiddenMacros={hasHiddenMacros}
                />
            ),
        );
    const bulkEntries = props.cardComponent ? (
        <>{bulkEntriesContent(() => true)}</>
    ) : (
        <div className={classNames("flex flex-col", INK)}>
            <div className="flex items-center gap-3 rounded-[14px] bg-white px-4 py-2.5">
                <span className="flex-1">
                    <span className="block text-[14px] font-normal">
                        {entriesWithValues.length} items
                    </span>
                    {hasHiddenMacros == false && (
                        <span className="text-[11.5px] text-[oklch(0.5_0.03_280)]">
                            Protein {mealCalculations.protein ?? 0} · Fat{" "}
                            {mealCalculations.fat ?? 0} · Carbs{" "}
                            {mealCalculations.carbs ?? 0}
                        </span>
                    )}
                </span>
                {hasHiddenMacros == false && (
                    <span className="text-[14px] font-light">
                        {mealCalculations.calories ?? "0"}{" "}
                        <span className="text-[11px] text-[oklch(0.55_0.02_280)]">
                            {useKilojoules ? "kJ" : "kcal"}
                        </span>
                    </span>
                )}
            </div>
            <div className="mt-7">{mealEditor.mealSelector}</div>
            <div className={classNames(CARD_LIST, "mt-6")}>
                {bulkEntriesContent(() => true)}
            </div>
        </div>
    );

    const singleExpandeRecipe = single.expanded_recipe;

    const singleQuantity =
        (single.quantity as string | null) == null
            ? null
            : new Decimal(single.quantity);
    const multiplier =
        singleQuantity == null || singleQuantity.isZero()
            ? new Decimal(1)
            : new Decimal(1).dividedBy(
                  singleQuantity
                      .dividedBy(single.container_servings)
                      .times(
                          single.units.find((u) => u.unit == single.unit)!.multiplier,
                      ),
              );

    const ingredients = singleExpandeRecipe.length > 0 && (
        <div className={classNames("flex flex-col", INK)}>
            <Headline
                icon={single.icon}
                name={single.name}
                note={
                    hasHiddenMacros ? (
                        `${singleExpandeRecipe.length} ingredients`
                    ) : (
                        <>
                            {singleExpandeRecipe.length} ingredients ·{" "}
                            <span className="font-medium text-[oklch(0.45_0.19_278)]">
                                {singleEntryCalculations.calories ?? "0"}{" "}
                                {useKilojoules ? "kJ" : "kcal"}
                            </span>{" "}
                            per serving
                        </>
                    )
                }
            />
            <div className={classNames(CARD_LIST, "mt-6")}>
                {singleExpandeRecipe.map((ingredient, index) => (
                    <StageRow
                        key={index}
                        entry={ingredient}
                        useKilojoules={useKilojoules}
                        hiddenMacros={hasHiddenMacros}
                        isRemoved={stage.values[ingredient.uuid]?.isRemoved ?? false}
                        onRemove={bindRemove(ingredient.uuid)}
                        quantity={{
                            unit: {
                                onChange: bindUnit(ingredient.uuid, ingredient.units),
                                value: ingredient.unit,
                                options: ingredient.units.map((unit) => unit.unit),
                            },
                            quantity: {
                                multiplier: multiplier.toString(),
                                onChange: bindQuantity(ingredient.uuid),
                                value: ingredient.quantity,
                            },
                        }}
                    />
                ))}
                {props.addIngredients != null && (
                    <button
                        type="button"
                        onClick={() => {
                            void hardware.haptic();
                            void props.addIngredients?.();
                        }}
                        className="block w-full py-[13px] text-left text-[13px] font-medium text-[oklch(0.45_0.19_278)]"
                    >
                        + Add ingredients
                    </button>
                )}
            </div>
        </div>
    );

    const singleEntry = (
        <div className={classNames("flex flex-col", INK)}>
            <Headline icon={single.icon} name={single.name} />
            <Label label={null} error={errors.quantity ?? null}>
                <Eyebrow className="mt-5 mb-[7px]">Serving</Eyebrow>
                <div className="flex flex-wrap items-center gap-1.5">
                    <ServingQuantity
                        value={single.quantity}
                        onChange={bindQuantity(single.uuid)}
                    />
                    <ServingUnit
                        entry={single}
                        onChange={bindUnit(single.uuid, single.units)}
                    />
                    {shouldShowWholeContainer === true && (
                        <Bubble
                            label="I ate the whole thing"
                            ghost
                            selected={false}
                            disabled={hasWholeContainerQuantity}
                            onClick={() => {
                                void analytics.track("CLICKED_WHOLE_CONTAINER", {
                                    whole_container_unit: single.unit,
                                });
                                bindQuantity(single.uuid)(wholeContainer.toString());
                            }}
                        />
                    )}
                </div>
            </Label>
            <div className="mt-[18px]">{mealEditor.mealSelector}</div>
            <div className="mt-[18px]">{entryMetrics}</div>
        </div>
    );

    const validate = () => {
        const validatedMeal = mealEditor.validateMeal();
        let hasError = false;

        if (
            entriesWithValues.length == 1 &&
            (entriesWithValues[0].quantity as string | null) == null
        ) {
            setErrors({...errors, quantity: "A quantity is required"});
            hasError = true;
        }
        if (validatedMeal === false || hasError) {
            return null;
        }

        return {
            meal: validatedMeal,
            entries: entriesWithValues
                .filter(
                    (staged) =>
                        stage.values[staged.uuid]?.isRemoved != true &&
                        stage.values[staged.uuid]?.quantity != null,
                )
                .map((e) => ({
                    ...e,
                    expanded_recipe: e.expanded_recipe.filter(
                        (ingredient) =>
                            stage.values[ingredient.uuid]?.isRemoved != true &&
                            stage.values[ingredient.uuid]?.quantity != null,
                    ),
                })),
            single: produce(single, (draftState) => {
                draftState.expanded_recipe = draftState.expanded_recipe.filter(
                    (ingredient) =>
                        stage.values[ingredient.uuid]?.isRemoved != true &&
                        stage.values[ingredient.uuid]?.quantity != null,
                );
            }),
        };
    };

    return {
        entryMetrics,
        mealSelector: mealEditor.mealSelector,
        mealModeToggle: mealEditor.mealModeToggle,
        data: stage,
        entries: stage.entries.length > 1 ? bulkEntries : singleEntry,
        singleEntry,
        bulkEntries,
        ingredients,
        hasErrors,
        validateMeal: mealEditor.validateMeal,
        validate,
        calculations: mealCalculations,
        setEntries,
        syncEntries,
        entriesWithValues,
        addEntry,
        removeEntry,
        setMethod: (method) => {
            setStage((prev) => ({...prev, method}));
        },
        bulkEntriesContent,
        setPredictionSelection,
        updatePredictionPattern,
    };
}
